import './styles.scss';

import { faCirclePlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import TableGrid from '../../../../components/table-grid';
import { CurrencyTypeEnum } from '../../../../model/currency_type.enum';
import { SnapshotDataTypeEnum } from '../../../../model/snapshot_data_type.enum';
import { SnapshotTypeEnum } from '../../../../model/snapshot_type';
import { MarketDataContext } from '../../../../providers/market-data/market-data-provider';
import { LatestVolSurfaceResponse } from '../../../../providers/model/interfaces/snapshot_data';
import { OverviewContext } from '../../../../providers/overview-provider';
import { SnapshotContext } from '../../../../providers/snapshot-provider';
import usePersistentState from '../../../../providers/use-persistent-state';
import Utils from '../../../../utils/immersive_utils';
import { makeSeriesLabel } from '../../../../utils/string';
import { getCurrentDate } from '../../../../utils/date';
import InlineOptions from '../../inline-options';
import Divider from '../../divider';
import VolatilitySmileContent from '../volatility-smile/volatility-smile-content';
import { GetVolProvider } from '../../../../providers/get-vol-provider';
import { VolSmileDataType, VolSmileProvider } from '../../../../providers/widgets';
import { DataLabelEnum } from '../../../../model/data_label.enum';
import { CustomTabLayoutContext } from '../../../custom-tab/custom-tab-layout-context';
import classNames from 'classnames';
import { TimestampCalendarPicker } from '../../calendar/timestamp-calendar-picker';
import { useDvol } from './use-dvol';
import { buildRequestTenors, formatDvol, pinTo0800Utc, rowTenorToDays } from './dvol-utils';

const SNAPSHOT_DATA = 'snapshot_data';
const HISTORIC_DATA = 'historic_data';
const CMVOL = 'CMVOL_';

type VolatilitySurfaceContentProps = {
  providerKey?: string;
  isCustomWidget: boolean;
};

const VolatilitySurfaceContent = ({
  providerKey,
  isCustomWidget = false,
}: VolatilitySurfaceContentProps) => {
  const {
    socketEvent,
    loading,
    setLoading,
    getData,
    token,
    currentSnapshotID,
    unsubscribe,
    type,
    snapshotType,
    setSnapshotType,
    isLiveData,
  } = useContext(SnapshotContext);
  const {
    currentTimeStamp,
    setTimeStamp,
    changeLayoutHeight,
    showVolSurfaceSmile,
    setShowVolSurfaceSmile,
    currency,
  } = useContext(OverviewContext);
  const { changeLayoutHeight: changeLayoutHeightCustom, markVolSurfaceSmileExpanded } =
    useContext(CustomTabLayoutContext);

  const generateCompositeId = (id: string) => {
    return Utils.generateCompositeIdWithPrefix(id, currency, providerKey);
  };

  const { series, addSeries, isFetchingCmvolSeries, fetchVolsHistorical } =
    useContext(MarketDataContext);

  const STATE_PATH = [
    `DASHBOARD_${currency}`,
    isCustomWidget ? `VOL_SURFACE_${providerKey}` : `VOL_SURFACE`,
  ];

  const [dataSource, setDataSource] = useState<any>([]);
  const [dataType, setDataType] = usePersistentState<SnapshotDataTypeEnum>(
    STATE_PATH,
    'dataType',
    SnapshotDataTypeEnum.PC,
  );
  const [visible, setVisible] = useState(true);

  // --- DVOL column (IM-dvol) ------------------------------------------------
  // Strictly a side-channel: derived from the already-rendered rows and the
  // displayed timestamp, NOT from the table's SnapshotContext/socketEvent data
  // path. Its loading/error state never gates the table's render.
  // Pin to 08:00 UTC as a NUMBER, not a Date. currentTimeStamp changes identity
  // every live tick even when the pinned 08:00 instant is unchanged; keying the
  // memo on the primitive ms keeps pinnedRef (and therefore getColumns) stable
  // across ticks instead of re-rendering the table each second.
  const pinMs = useMemo(() => {
    const pinned = pinTo0800Utc(currentTimeStamp);
    return pinned ? pinned.getTime() : null;
  }, [currentTimeStamp]);
  const pinnedRef = useMemo(() => (pinMs != null ? new Date(pinMs) : null), [pinMs]);

  const requestTenors = useMemo(() => {
    // Dispatch on the type ref (not snapshotType state) so a mode toggle can't
    // transiently mis-dispatch — the table path (setTable/renderCell) keys on
    // type.current too. snapshotType stays in the dep list as the recompute
    // trigger; it and type.current flip together on a mode change.
    const days = (dataSource ?? [])
      .map((row: any) => rowTenorToDays(row?.tenor, type.current, pinnedRef))
      .filter((d: number | null): d is number => d != null);
    // EE rows are the listed expiries, so the longest can't be bracketed and
    // would fail the whole request (IM-2602); drop it. CM points sit inside the
    // ladder and self-correct via the hook's retry-drop-largest, so keep them all.
    return buildRequestTenors(days, type.current === SnapshotTypeEnum.EE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, snapshotType, pinMs]);

  const { dvolByTenor } = useDvol(token, pinnedRef, requestTenors);

  const getColumns = useCallback(
    ({ visible }: any) => {
      const renderCell = (params: any) => {
        const defaultRender = <div className="snapshot-default-item">{params.value}</div>;

        if (type.current === SnapshotTypeEnum.EE || isCustomWidget) return defaultRender;

        const colField = params.colDef?.field;

        if (!colField || colField === 'tenor') return defaultRender;

        const id = `${CMVOL}${colField.toUpperCase()}_${params?.data?.tenor}_${currency}`;

        const alreadyExists = Object.values(series).some((s) => s.id === id);

        if (alreadyExists) {
          return defaultRender;
        }

        return (
          <Tooltip title="Add to chart" arrow classes={{ tooltip: 'vol-surface-add-tooltip' }}>
            <div
              className={`snapshot-cell-item  ${isFetchingCmvolSeries ? 'loading-vol' : ''}`}
              onClick={() => handleCellClick(id)}
            >
              <div className="val">{params.value}</div>
              <div className="icon">
                <FontAwesomeIcon icon={faCirclePlus} className="icon" />
              </div>
            </div>
          </Tooltip>
        );
      };

      // Null-safe DVOL cell: computes the row's day count and looks it up in the
      // side-channel map. Blanks for EVERY miss reason — unsupported token,
      // dropped last expiry (IM-2602), pre-history timestamp, no-data, in-flight
      // or error — and never throws into the grid.
      const renderDvolCell = (params: any) => {
        let text = '';
        try {
          const days = rowTenorToDays(params?.data?.tenor, type.current, pinnedRef);
          if (days != null) text = formatDvol(dvolByTenor[days]);
        } catch {
          text = '';
        }
        return <div className="snapshot-default-item">{text}</div>;
      };

      const columns: any[] = [
        {
          field: 'tenor',
          headerName: type.current === SnapshotTypeEnum.EE ? 'Expiry' : 'Tenor',
          sortable: false,
          minWidth: 125,
        },
        {
          field: 'Future',
          headerName: 'Future',
          sortable: false,
          cellRenderer: renderCell,
          minWidth: 125,
          align: 'right',
        },
        {
          field: '10P',
          headerName: '10P',
          sortable: false,
          cellRenderer: renderCell,
          hide: !visible,
          align: 'right',
        },
        {
          field: '25P',
          headerName: '25P',
          sortable: false,
          cellRenderer: renderCell,
          hide: !visible,
          align: 'right',
        },
        {
          field: 'ATMF',
          headerName: 'ATMF',
          sortable: false,
          cellRenderer: renderCell,
          align: 'right',
        },
        {
          field: '25C',
          headerName: '25C',
          sortable: false,
          cellRenderer: renderCell,
          hide: !visible,
          align: 'right',
        },
        {
          field: '10C',
          headerName: '10C',
          sortable: false,
          cellRenderer: renderCell,
          hide: !visible,
          align: 'right',
        },
        {
          field: '25FLY',
          headerName: '25FLY',
          sortable: false,
          cellRenderer: renderCell,
          hide: visible,
          align: 'right',
        },
        {
          field: '25RR',
          headerName: '25RR',
          sortable: false,
          cellRenderer: renderCell,
          hide: visible,
          align: 'right',
        },
        {
          field: '10FLY',
          headerName: '10FLY',
          sortable: false,
          cellRenderer: renderCell,
          hide: visible,
          align: 'right',
        },
        {
          field: '10RR',
          headerName: '10RR',
          sortable: false,
          cellRenderer: renderCell,
          hide: visible,
          align: 'right',
        },
        {
          // DVOL is an index, independent of the PUT/CALL vs RR/FLY view, so it
          // is always visible and appended last in both.
          field: 'DVOL',
          headerName: 'DVOL',
          sortable: false,
          cellRenderer: renderDvolCell,
          align: 'right',
        },
      ];
      return columns.filter((col) => !col.hide);
    },
    [series, dataType, type, isFetchingCmvolSeries, snapshotType, dvolByTenor, pinMs],
  );

  const [isHorizontalLayout, setIsHorizontalLayout] = usePersistentState(
    STATE_PATH,
    'isHorizontalLayout',
    false,
  );
  const containerRef = useRef();

  const percentFormat = (data: string): string => {
    return (
      parseFloat((parseFloat(data) * 100).toFixed(2)).toLocaleString(undefined, {
        minimumFractionDigits: 2,
      }) + '%'
    );
  };

  const handleCellClick = useCallback(
    (id: string) => {
      if (isFetchingCmvolSeries) return;

      if (type.current === SnapshotTypeEnum.CM) {
        const alreadyExists = Object.values(series).some((s) => s.id === id);

        if (!alreadyExists) {
          const rawId = id;
          const type = rawId.includes('FLY') || rawId.includes('RR') ? 'fly' : 'vol';
          const label = makeSeriesLabel(rawId);
          addSeries({
            rawId,
            type,
            label,
            seriesVolsCallback: (queries) =>
              fetchVolsHistorical({
                endDate: getCurrentDate(),
                queries,
                options: { isNewVol: true },
              }),
          });
        }
      }
    },
    [addSeries, type, isFetchingCmvolSeries, fetchVolsHistorical],
  );

  const setTable = useCallback(
    (data: LatestVolSurfaceResponse) => {
      const getDecimalPoints = () => {
        return currency === CurrencyTypeEnum.XRP ? 4 : 2;
      };

      try {
        const gridData: any[] = [];

        if (data.result && typeof data.result === 'number') return;

        let timestamp;
        let snapshot_time;
        let tenors;

        if (data.params) {
          timestamp = data?.params?.data?.timestamp;
          snapshot_time = data?.params?.data?.snapshot_time;
          tenors = data?.params?.data?.tenors;
        } else {
          timestamp = data?.result?.timestamp;
          snapshot_time = data?.result?.snapshot_time;
          tenors = data?.result?.tenors;
        }

        setDataSource(gridData);

        setTimeStamp(timestamp ?? snapshot_time);

        if (tenors) {
          for (const [key] of Object.entries(tenors)) {
            const gridRow: { [key: string]: string } = {};
            gridRow['tenor'] = key;
            for (const [volName, volData] of Object.entries(tenors[key])) {
              const decimalPoints = getDecimalPoints();

              gridRow[volName] =
                volName === 'Future'
                  ? parseFloat(parseFloat(volData as string).toFixed(decimalPoints)).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: decimalPoints,
                      },
                    )
                  : percentFormat(volData as string);
            }
            gridData.push(gridRow);
          }
        }

        if (type.current === SnapshotTypeEnum.CM) {
          Utils.sortTenors(gridData);
        }

        setDataSource(gridData);
      } catch {
        return;
      }
    },
    [setTimeStamp, type, currency],
  );

  const handleApply = (timestamp: string) => {
    if (!timestamp) return;
    setTimeStamp(timestamp);
    try {
      unsubscribe(token);
    } catch {
      /* noop */
    }
    currentSnapshotID.current = 'historic_data';
    getData(timestamp);
  };

  const handleReset = () => {
    const now = new Date().toISOString();
    setTimeStamp(now);
    try {
      unsubscribe(token);
    } catch {
      /* noop */
    }
    currentSnapshotID.current = 'snapshot_data';
    getData(now);
  };
  const handleResetDateTime = useCallback(() => {
    const date = new Date().toISOString();

    setLoading(true);
    unsubscribe(generateCompositeId(SNAPSHOT_DATA) as CurrencyTypeEnum);
    setTimeStamp(date);
    currentSnapshotID.current = SNAPSHOT_DATA;
    getData(date);
  }, [currentSnapshotID, getData, setLoading, setTimeStamp, unsubscribe]);

  const updateType = (i: SnapshotTypeEnum) => {
    type.current = i;
    setLoading(true);
    unsubscribe(generateCompositeId(SNAPSHOT_DATA) as CurrencyTypeEnum);

    if (currentSnapshotID.current === HISTORIC_DATA) {
      getData(currentTimeStamp);
    } else {
      getData(currentTimeStamp);
    }
  };

  const create = useCallback(
    (data: any) => {
      setLoading(false);
      setTable(data);
    },
    [setLoading, setTable],
  );

  const handleVolSurfaceSmileVisibility = useCallback(() => {
    const newValue = !showVolSurfaceSmile;

    const values = [
      { size: 'lg', value: newValue ? 7 : 4 },
      { size: 'md', value: newValue ? 7 : 4 },
      { size: 'sm', value: newValue ? 8 : 4 },
      { size: 'xs', value: newValue ? 8 : 4 },
      { size: 'xxs', value: newValue ? 8 : 4 },
    ];

    if (isCustomWidget && changeLayoutHeightCustom && providerKey) {
      changeLayoutHeightCustom(providerKey, values);
      if (markVolSurfaceSmileExpanded) {
        markVolSurfaceSmileExpanded(providerKey, newValue);
      }
    } else {
      changeLayoutHeight('snapshot-data', values);
    }

    setShowVolSurfaceSmile(newValue);
  }, [
    changeLayoutHeight,
    changeLayoutHeightCustom,
    isCustomWidget,
    providerKey,
    setShowVolSurfaceSmile,
    showVolSurfaceSmile,
  ]);

  useEffect(() => {
    if (socketEvent) {
      create(socketEvent);
    }
  }, [create, socketEvent]);

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      const { contentRect } = entries[0];

      setIsHorizontalLayout(contentRect.width >= 1000);
    });

    if (containerRef.current) {
      ro.observe(containerRef.current);
    }
  }, [containerRef]);

  useEffect(() => {
    setVisible(dataType === SnapshotDataTypeEnum.PC);
  }, [dataType]);

  const getBody = useMemo(() => {
    const renderSmile = showVolSurfaceSmile && !!socketEvent;

    const columns = getColumns({ visible });

    let tenors: any;

    if (socketEvent?.params) {
      tenors = socketEvent.params?.data?.tenors;
    } else if (socketEvent?.result) {
      tenors = socketEvent.result?.tenors;
    } else {
      tenors = null;
    }

    return (
      <div className={`snapshot-body ${isFetchingCmvolSeries ? 'snapshot-loading-vol' : ''}`}>
        <div className={classNames('snapshot-data-container', { 'has-vol-smiles': renderSmile })}>
          {loading ? (
            <div className="snapshot-body-loading">
              <CircularProgress />
            </div>
          ) : (
            <TableGrid
              columns={columns}
              rowData={dataSource}
              className="snapshop-data"
              autoHeight={renderSmile}
            />
          )}
        </div>
        {renderSmile && (
          <GetVolProvider label={DataLabelEnum.PERPETUAL}>
            <VolSmileProvider
              dataType={VolSmileDataType.volSmile}
              providerKey={providerKey || 'vol-surface'}
            >
              <VolatilitySmileContent
                showHeader={false}
                isSnapshotData
                snapshotData={tenors}
                dataType={snapshotType}
                classname="vol-surface-smile-container"
                providerKey={providerKey || 'vol-surface'}
              />
            </VolSmileProvider>
          </GetVolProvider>
        )}
      </div>
    );
  }, [
    currency,
    dataSource,
    getColumns,
    handleApply,
    handleCellClick,
    handleResetDateTime,
    isHorizontalLayout,
    isLiveData,
    showVolSurfaceSmile,
    socketEvent,
    visible,
    dataType,
    snapshotType,
    loading,
  ]);

  return (
    <Box className="snapshot-wrapper" ref={containerRef}>
      <div className="vol-surface-header">
        <div className="vol-surface-header-top">
          <div className="vol-surface-header-options">
            <InlineOptions
              options={[
                { value: SnapshotTypeEnum.EE, label: 'Expiry' },
                { value: SnapshotTypeEnum.CM, label: 'Tenor' },
              ]}
              selectedOption={snapshotType}
              onOptionChange={(val: string | string[]) => {
                setSnapshotType(val as SnapshotTypeEnum);
                updateType(val as SnapshotTypeEnum);
              }}
              optionDisabled={loading || isFetchingCmvolSeries}
            />
            <Divider />

            <InlineOptions
              options={[
                { value: SnapshotDataTypeEnum.PC, label: 'PUT/CALL' },
                { value: SnapshotDataTypeEnum.RF, label: 'RR/FLY' },
              ]}
              selectedOption={dataType}
              onOptionChange={(val: string | string[]) => {
                setVisible(false);
                setDataType(val as SnapshotDataTypeEnum);
              }}
            />
          </div>
          <Button onClick={handleVolSurfaceSmileVisibility} className="btn-visibility-vol-surface">
            <span>{showVolSurfaceSmile ? 'Hide' : 'Show'} Vol Smiles</span>
          </Button>
        </div>
        <div className="vol-surface-calendar">
          <TimestampCalendarPicker
            handleChangeDatetime={handleApply}
            isLive={isLiveData}
            reset={handleReset}
            displayTimestamp={currentTimeStamp}
            inputIsUTC
          />
        </div>
      </div>

      {getBody}
    </Box>
  );
};

export default VolatilitySurfaceContent;
